import { faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import MultipleChoiceOption from 'models/MultipleChoiceOption';
import Question from 'models/Question';
import React, { useCallback } from 'react';
import { FileInput } from '../FileInputs';
import IconButton from 'view/components/buttons/IconButton';

interface MultipleChoiceOptionItemProps {
  question: Question;
  choice: MultipleChoiceOption;
  index: number;
  allowImages: boolean;
  isDefaultView: boolean;
  allowCorrectChoices?: boolean;
  onDrop: (e: React.DragEvent<HTMLDivElement>, toIndex: number) => void;
  onModifyQuestionChoice: (
    question: Question,
    choice: MultipleChoiceOption,
    text: string,
    correct: boolean,
  ) => void;
  onChoiceImageUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
    choice: any,
  ) => void;
  onDeleteQuestionChoice: (
    question: Question,
    choice: MultipleChoiceOption,
  ) => void;
}

const MultipleChoiceOptionItem: React.FC<MultipleChoiceOptionItemProps> = ({
  question,
  choice,
  index,
  allowImages,
  isDefaultView,
  allowCorrectChoices = true,
  onDrop,
  onChoiceImageUpload,
  onModifyQuestionChoice,
  onDeleteQuestionChoice,
}) => {
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, idx: number) => {
      e.dataTransfer.setData('text/plain', idx.toString());
      e.dataTransfer.effectAllowed = 'move';
    },
    [],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // necessary to allow drop
  }, []);

  return (
    <div
      key={choice.id}
      className="multiple-choice-option"
      draggable
      onDragStart={(e) => handleDragStart(e, index)}
      onDragOver={handleDragOver}
      onDrop={(e) => onDrop(e, index)}
    >
      {isDefaultView ? (
        <>
          {allowCorrectChoices && (
            <input
              type="checkbox"
              checked={choice.correct}
              onChange={(ev) =>
                onModifyQuestionChoice(
                  question,
                  choice,
                  choice.text,
                  ev.target.checked,
                )
              }
            />
          )}
          <textarea
            value={choice.text}
            onChange={(ev) =>
              onModifyQuestionChoice(
                question,
                choice,
                ev.target.value,
                choice.correct,
              )
            }
            maxLength={100}
          />
          <div className="action-buttons" style={{ gap: '2px' }}>
            {allowImages && (
              <FileInput
                id={`choice-image-${question.index}-${choice.id}`}
                label=""
                accept=".jpeg,.jpg,.png"
                type="inline"
                icon={faImage}
                onFileChange={(ev) => onChoiceImageUpload(ev, choice)}
                title="Upload an image for this option"
              />
            )}
            <IconButton
              icon={faTrash}
              type="transparent"
              onClick={() => onDeleteQuestionChoice(question, choice)}
              color="#B1243F"
            />
          </div>
        </>
      ) : (
        <div className="row">
          <input
            type="checkbox"
            checked={choice.correct}
            onChange={(ev) =>
              onModifyQuestionChoice(
                question,
                choice,
                choice.text,
                ev.target.checked,
              )
            }
          />
          <div className="multiple-choice-img">
            <img src={choice.img_src ?? choice.img} alt="" />
          </div>
          <div className="action-buttons">
            <FileInput
              id={`choice-image-${question.index}-${choice.id}`}
              accept="image/*"
              type="inline"
              icon={faImage}
              onFileChange={(ev) => onChoiceImageUpload(ev, choice)}
            />
            <IconButton
              icon={faTrash}
              type="transparent"
              onClick={() => onDeleteQuestionChoice(question, choice)}
              color="#B1243F"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleChoiceOptionItem;
